import { getUrlDomain, isWebActivityItem } from './sourceIcons'

export type WebActivityLabelMode =
  | 'matterEntry'
  | 'matterSlot'
  | 'timeSlot'
  | 'activity'
  | 'activityGroup'

export interface WebActivityRecord {
  apps?: string[] | null
  external_links?: Array<{ url?: string | null }> | null
  metadata?: unknown
  non_svc_source?: string | null
  slots?: WebActivityRecord[] | null
  worked_on?: string | null
}

const URL_LABEL_MODES = new Set<WebActivityLabelMode>([
  'matterEntry',
  'timeSlot',
  'activity',
  'activityGroup',
])

function stripHtml(value?: string | null): string {
  if (!value) return ''
  return value.replace(/<[^>]+>/g, '').trim()
}

function uniqueTerms(terms: string[]): string[] {
  const seen = new Set<string>()

  return terms.filter(term => {
    if (seen.has(term)) return false
    seen.add(term)
    return true
  })
}

export function getWebQueryTerms(
  item?: { metadata?: unknown } | null
): string[] {
  const metadata = item?.metadata
  if (!metadata || typeof metadata !== 'object') return []

  const webInfo = (metadata as { web_info?: unknown }).web_info
  if (!webInfo || typeof webInfo !== 'object') return []

  const source = (webInfo as { source?: unknown }).source
  if (!source || typeof source !== 'object') return []

  const terms = (source as { query_term?: unknown }).query_term
  if (!Array.isArray(terms)) return []

  return uniqueTerms(
    terms
      .filter((term): term is string => typeof term === 'string')
      .map(term => term.trim())
      .filter(Boolean)
  )
}

export function getEntryWebQueryTerms(
  item?: WebActivityRecord | null
): string[] {
  const slots = Array.isArray(item?.slots) ? item.slots : []
  const slotTerms = slots.flatMap(slot => getWebQueryTerms(slot))
  if (slotTerms.length > 0) return uniqueTerms(slotTerms)
  return getWebQueryTerms(item)
}

export function getWebActivityTerms(
  item: WebActivityRecord | null | undefined,
  mode: WebActivityLabelMode
): string[] {
  if (mode === 'activityGroup' || mode === 'matterEntry') {
    return getEntryWebQueryTerms(item)
  }
  return getWebQueryTerms(item)
}

export function getWebActivityUrl(item?: WebActivityRecord | null): string {
  const ownUrl = (item?.external_links || []).find(link => link?.url)?.url
  if (ownUrl) return ownUrl

  for (const slot of item?.slots || []) {
    const slotUrl = (slot?.external_links || []).find(link => link?.url)?.url
    if (slotUrl) return slotUrl
  }

  return ''
}

export function getWebActivityDomain(item?: WebActivityRecord | null): string {
  const workedOn = stripHtml(item?.worked_on)
  if (workedOn) return workedOn

  return getUrlDomain(getWebActivityUrl(item))
}

export function formatWebActivityLabel(
  item: WebActivityRecord | null | undefined,
  mode: WebActivityLabelMode
): string {
  const domain = getWebActivityDomain(item)
  const terms = getWebActivityTerms(item, mode)

  if (terms.length === 0) return domain

  const researched = `Researched (${terms.length}): ${terms
    .map(term => `"${term}"`)
    .join(', ')}`

  if (!URL_LABEL_MODES.has(mode)) return researched
  return domain ? `${domain} · ${researched}` : researched
}

export function shouldUseWebActivityLabel(
  item: WebActivityRecord | null | undefined,
  mode?: WebActivityLabelMode | null
): boolean {
  if (!item || !mode || !isWebActivityItem(item)) return false
  if (mode !== 'matterEntry') return true

  const slots = Array.isArray(item.slots) ? item.slots : []
  if (!slots.length) return true
  return slots.every(slot => isWebActivityItem(slot))
}
